import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';

export default function Consent() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    api.get(`/consent/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ error: true }));
  }, [token]);

  const respond = async (approve) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/consent/${token}/respond`, { approve });
      setResult(data.status);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-lg w-full gold-glow"
        data-testid="consent-page">
        {!info && <p className="text-brume" data-testid="consent-loading">{t('common.loading')}</p>}
        {info?.error && (
          <p className="text-rose-400 font-bold" data-testid="consent-error">
            {t('consent.error')}
          </p>
        )}
        {info && !info.error && !result && info.status === 'pending' && (
          <>
            <span className="w-14 h-14 rounded-2xl bg-or-400/15 flex items-center justify-center mb-6">
              <ShieldCheck className="w-7 h-7 text-or-400" strokeWidth={2.5} />
            </span>
            <h1 className="text-2xl sm:text-3xl font-bold text-creme mb-4">{t('consent.title')}</h1>
            <p className="text-base text-brume leading-relaxed mb-2">
              <strong className="text-creme">{info.youth_name}</strong>
              {info.youth_age ? t('consent.ageSuffix', { age: info.youth_age }) : ''}{' '}
              {t('consent.intro', { name: '', age: '' }).trim()}
            </p>
            <ul className="text-sm text-brume leading-relaxed list-disc pl-5 space-y-2 my-6">
              <li>{t('consent.b1')}</li>
              <li>{t('consent.b2')}</li>
              <li>{t('consent.b3')}</li>
              <li>{t('consent.b4')}</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="consent-approve-button" onClick={() => respond(true)} disabled={loading}
                className="flex-1 h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
                {t('consent.approve')}
              </Button>
              <Button data-testid="consent-refuse-button" onClick={() => respond(false)} disabled={loading}
                variant="outline"
                className="flex-1 h-14 rounded-full border-2 border-nuit-600 text-brume font-bold text-base hover:bg-rose-950 hover:text-rose-300 hover:border-rose-800 bg-transparent">
                {t('consent.refuse')}
              </Button>
            </div>
          </>
        )}
        {(result === 'approved' || (info && !info.error && !result && info.status === 'approved')) && (
          <div data-testid="consent-approved-message" className="text-center">
            <CheckCircle2 className="w-16 h-16 text-or-400 mx-auto mb-6" strokeWidth={2} />
            <h1 className="text-2xl sm:text-3xl font-bold text-creme mb-4">{t('consent.approvedTitle')}</h1>
            <p className="text-base text-brume leading-relaxed">
              {t('consent.approvedText', { name: info?.youth_name, email: info?.parent_email })}
            </p>
          </div>
        )}
        {(result === 'refused' || (info && !info.error && !result && info.status === 'refused')) && (
          <div data-testid="consent-refused-message" className="text-center">
            <XCircle className="w-16 h-16 text-rose-400 mx-auto mb-6" strokeWidth={2} />
            <h1 className="text-2xl sm:text-3xl font-bold text-creme mb-4">{t('consent.refusedTitle')}</h1>
            <p className="text-base text-brume leading-relaxed">
              {t('consent.refusedText')}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
